import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { ScrollArea } from '../ui/scroll-area'
import { toast } from 'sonner'
import { useLanguage } from '../../context/LanguageContext'
import { COLOR_OPTIONS } from '../../lib/utils'
import { FINANCE_ICON_OPTIONS, getIconLabel } from '../../lib/iconOptions'
import { 
  Plus, ArrowUpCircle, ArrowDownCircle, Circle, Home, Utensils, Car, Heart,
  GraduationCap, Gamepad2, CreditCard, Shirt, BadgeDollarSign, TrendingUp,
  PiggyBank, Gift, Briefcase, Building2, Zap, Layers, ChevronRight,
  // New icons
  ShoppingCart, UtensilsCrossed, Bus, Fuel, ShieldCheck, RefreshCw, Wifi,
  Smartphone, Plane, Palmtree, FileText, CircleDollarSign, Landmark,
  HeartHandshake, PawPrint, Baby, Laptop, Wrench, AlertTriangle, Coffee,
  Beer, Dumbbell, Pill, Scissors, Music, Tv,
  // Servicios del hogar
  Lightbulb, Droplets, Flame, Phone
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Extended icon map with all finance icons
const iconMap = {
  // Original icons
  Home, Utensils, Car, Heart, GraduationCap, Gamepad2, CreditCard, Shirt,
  BadgeDollarSign, TrendingUp, PiggyBank, Gift, Briefcase, Building2, Zap, Circle,
  // New icons
  ShoppingCart, UtensilsCrossed, Bus, Fuel, ShieldCheck, RefreshCw, Wifi,
  Smartphone, Plane, Palmtree, FileText, CircleDollarSign, Landmark,
  HeartHandshake, PawPrint, Baby, Laptop, Wrench, AlertTriangle, Coffee,
  Beer, Dumbbell, Pill, Scissors, Music, Tv,
  // Servicios del hogar
  Lightbulb, Droplets, Flame, Phone
}

/**
 * Reusable modal for creating/editing finance categories
 * Can be used from SettingsPage and FinanzasPage
 */
export function FinanceCategoryModal({ 
  open, 
  onOpenChange, 
  editingCategory = null,
  onSuccess,
  defaultType = 'expense'
}) {
  const { locale, t } = useLanguage()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '',
    icon: 'Circle',
    color: '#6b7280',
    category_type: defaultType
  })

  // Reset form when modal opens/closes or editing changes
  useEffect(() => {
    if (open) {
      if (editingCategory) {
        setForm({
          name: editingCategory.name,
          icon: editingCategory.icon || 'Circle',
          color: editingCategory.color || '#6b7280',
          category_type: editingCategory.category_type || 'expense'
        })
      } else {
        setForm({
          name: '',
          icon: 'Circle',
          color: '#6b7280',
          category_type: defaultType
        })
      }
    }
  }, [open, editingCategory, defaultType])

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('settings.nameRequired'))
      return
    }

    if (saving) return
    setSaving(true)

    try {
      if (editingCategory) {
        await axios.put(`${API}/finance/categories/${editingCategory.id}`, form)
        toast.success(t('settings.categoryUpdated'))
      } else {
        await axios.post(`${API}/finance/categories`, form)
        toast.success(t('settings.categoryCreated'))
      }
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      const message = error.response?.data?.detail || t('finance.errorSaving')
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingCategory 
              ? t('categoryModals.editFinanceCategory')
              : t('categoryModals.newFinanceCategory')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('common.name')} *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder={locale === 'es-MX' ? 'Ej: Supermercado' : 'E.g.: Groceries'}
              data-testid="finance-category-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>{t('common.type')} *</Label>
            <Select 
              value={form.category_type} 
              onValueChange={(value) => setForm(prev => ({ ...prev, category_type: value }))}
            >
              <SelectTrigger data-testid="finance-category-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expense">
                  <div className="flex items-center gap-2">
                    <ArrowDownCircle className="w-4 h-4 text-rose-500" />
                    {t('categoryModals.expenseType')}
                  </div>
                </SelectItem>
                <SelectItem value="income">
                  <div className="flex items-center gap-2">
                    <ArrowUpCircle className="w-4 h-4 text-emerald-500" />
                    {t('categoryModals.incomeType')}
                  </div>
                </SelectItem>
                <SelectItem value="both">
                  <div className="flex items-center gap-2">
                    <Circle className="w-4 h-4 text-sky-500" />
                    {t('categoryModals.bothType')}
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('common.icon')}</Label>
            <Select 
              value={form.icon} 
              onValueChange={(value) => setForm(prev => ({ ...prev, icon: value }))}
            >
              <SelectTrigger data-testid="finance-category-icon-select">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const SelectedIcon = iconMap[form.icon] || Circle
                      return <SelectedIcon className="w-4 h-4" />
                    })()}
                    {getIconLabel(FINANCE_ICON_OPTIONS.find(i => i.id === form.icon), locale) || t('finance.other')}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <ScrollArea className="h-[280px]">
                  {FINANCE_ICON_OPTIONS.map((icon) => {
                    const IconComponent = iconMap[icon.id] || Circle
                    return (
                      <SelectItem key={icon.id} value={icon.id}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          {getIconLabel(icon, locale)}
                        </div>
                      </SelectItem>
                    )
                  })}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('common.color')}</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setForm(prev => ({ ...prev, color }))}
                  className={`w-8 h-8 rounded-full transition-all ${
                    form.color === color ? 'ring-2 ring-offset-2 ring-foreground scale-110' : ''
                  }`}
                  style={{ backgroundColor: color }}
                  data-testid={`finance-color-${color}`}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="pt-4 border-t">
            <Label className="text-xs text-muted-foreground">
              {t('common.preview')}
            </Label>
            <div 
              className="mt-2 p-4 rounded-xl flex items-center gap-3"
              style={{ backgroundColor: `${form.color}20` }}
            >
              {(() => {
                const PreviewIcon = iconMap[form.icon] || Circle
                return <PreviewIcon className="w-8 h-8" style={{ color: form.color }} />
              })()}
              <div>
                <span className="font-bold">{form.name || t('settings.addCategory')}</span>
                <span className="block text-xs text-muted-foreground">
                  {form.category_type === 'income' 
                    ? t('categoryModals.incomeType')
                    : form.category_type === 'expense' 
                      ? t('categoryModals.expenseType')
                      : t('categoryModals.bothType')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-finance-category-btn">
            {saving 
              ? t('common.saving') 
              : editingCategory 
                ? t('common.save')
                : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Reusable modal for creating/editing finance subcategories
 */
export function FinanceSubcategoryModal({
  open,
  onOpenChange,
  categoryId,
  categoryName,
  editingSubcategory = null,
  onSuccess
}) {
  const { locale, t } = useLanguage()
  const [saving, setSaving] = useState(false)
  const [name, setName] = useState('')

  useEffect(() => {
    if (open) {
      setName(editingSubcategory?.name || '')
    }
  }, [open, editingSubcategory])

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t('settings.nameRequired'))
      return
    }

    if (saving) return
    setSaving(true)

    try {
      const payload = { name: name.trim() }
      
      if (editingSubcategory) {
        await axios.put(`${API}/finance/subcategories/${editingSubcategory.id}`, payload)
        toast.success(t('settings.subcategoryUpdated'))
      } else {
        await axios.post(`${API}/finance/subcategories`, {
          ...payload,
          category_id: categoryId
        })
        toast.success(t('settings.subcategoryCreated'))
      }
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      const message = error.response?.data?.detail || t('finance.errorSaving')
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingSubcategory
              ? t('categoryModals.editSubcategory')
              : t('categoryModals.newSubcategory')}
          </DialogTitle>
          <DialogDescription>
            {categoryName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('common.name')} *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={locale === 'es-MX' ? 'Ej: Supermercado' : 'E.g.: Groceries'}
              data-testid="finance-subcategory-name-input"
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-finance-subcategory-btn">
            {saving
              ? t('common.saving')
              : editingSubcategory
                ? t('common.save')
                : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Quick add button component for inline category creation
 */
export function QuickAddFinanceCategoryButton({ onCategoryCreated, defaultType = 'expense', className = '' }) {
  const { t } = useLanguage()
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors ${className}`}
        data-testid="quick-add-finance-category-btn"
      >
        <Plus className="w-3 h-3" />
        {t('common.newFem')}
      </button>
      
      <FinanceCategoryModal
        open={showModal}
        onOpenChange={setShowModal}
        defaultType={defaultType}
        onSuccess={onCategoryCreated}
      />
    </>
  )
}

/**
 * Quick add button for subcategories
 */
export function QuickAddFinanceSubcategoryButton({ 
  categoryId, 
  categoryName, 
  onSubcategoryCreated, 
  className = '' 
}) {
  const { t } = useLanguage()
  const [showModal, setShowModal] = useState(false)

  if (!categoryId) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors ${className}`}
        data-testid="quick-add-finance-subcategory-btn"
      >
        <Plus className="w-3 h-3" />
        {t('categoryModals.newSubcategory')}
      </button>
      
      <FinanceSubcategoryModal
        open={showModal}
        onOpenChange={setShowModal}
        categoryId={categoryId}
        categoryName={categoryName}
        onSuccess={onSubcategoryCreated}
      />
    </>
  )
}
